import React from 'react';

/**
 * Evolve page — colour-based prompts to expand perspective.
 */
export function EvolvePage(): React.JSX.Element {
  return (
    <div className="flex flex-col items-center px-4 py-16 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl">
        {/* Prism SVG */}
        <div className="mb-8 flex justify-center">
          <svg
            width="260"
            height="120"
            viewBox="0 0 260 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            className="opacity-90"
          >
            <line x1="0" y1="60" x2="85" y2="60" stroke="#1D1C1D" strokeWidth="3" strokeLinecap="round" />
            <polygon points="90,20 150,20 120,100" fill="none" stroke="#1D1C1D" strokeWidth="2" />
            <line x1="160" y1="28" x2="260" y2="8" stroke="#C75B5B" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="161" y1="35" x2="260" y2="25" stroke="#D4874D" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="162" y1="42" x2="260" y2="42" stroke="#C9A84C" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="162" y1="49" x2="260" y2="58" stroke="#5B9E6F" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="161" y1="56" x2="260" y2="75" stroke="#4D9E9E" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="160" y1="63" x2="260" y2="92" stroke="#4A7FB5" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="158" y1="70" x2="260" y2="108" stroke="#7B5EA7" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        </div>

        <h1 className="mb-2 text-center text-3xl font-bold text-text-primary">
          Start Here
        </h1>
        <p className="mb-3 text-center text-sm font-medium text-text-muted italic">
          Don&rsquo;t know where to start to shift your thinking and unlock better outcomes at work?
        </p>
        <p className="mb-12 text-center text-sm text-text-secondary leading-relaxed max-w-2xl mx-auto">
          Try any of these work‑focused thought experiments to get started. Choose one by colour, or explore the same prompt with all seven agents to experience how each lens reveals something different about your work situation.
        </p>

        {/* RED */}
        <ColourSection
          colour="#C75B5B"
          colourLabel="Red"
          think="What Have Your Difficulties Done For You?"
          consider="We grow, with new insight and opportunities, when we can reinterpret difficulty as development."
          reflect="Which challenge in your life has shaped you the most?"
          expandUrl="https://www.linkedin.com/posts/sukhmani-kaur-ms-odhr-8ba90533_what-have-your-difficulties-done-for-you-activity-7387850791816175616-SuPF"
          evolve='Ask Ray, AAI&rsquo;s Red agent: "What may be emerging from this difficulty that I can build on?" and see where that one question takes you.'
        />

        {/* ORANGE */}
        <ColourSection
          colour="#D4874D"
          colourLabel="Orange"
          think="The More I Know, The More I Know I Don't Know"
          consider="Eagerness is the energy of continuous learning — the willingness to be surprised."
          reflect="What's something you're newly curious about that you haven't explored yet?"
          expandUrl="https://www.linkedin.com/posts/sukhmani-kaur-ms-odhr-8ba90533_the-more-i-know-the-more-i-know-that-i-don-activity-7477774092956291072-K7w4"
          evolve='Ask Olympia, AAI&rsquo;s Orange agent: "Help me explore this without needing answers yet, just to see what makes me eager and fills me with energy." and see where that one question takes you.'
        />

        {/* YELLOW */}
        <ColourSection
          colour="#C9A84C"
          colourLabel="Yellow"
          think="Better Already Exists"
          consider="Readiness isn't about perfection; it's about entering the arena while the future is still forming."
          reflect="Where in your work are you waiting for conditions that will never be perfect?"
          expandUrl="https://www.linkedin.com/posts/sukhmani-kaur-ms-odhr-8ba90533_better-already-exists-jump-in-we-dont-activity-7481387136315719681-ezO9"
          evolve='Ask Yves, AAI&rsquo;s Yellow agent: "What&rsquo;s the smallest courageous step I can take today?" and see where that one question takes you.'
        />

        {/* GREEN */}
        <ColourSection
          colour="#5B9E6F"
          colourLabel="Green"
          think="How Do Your People Interconnect for Extraordinary Outcomes?"
          consider="Extraordinary performance is not individual — it's relational architecture."
          reflect="Where does your organization's flow break down between people?"
          expandUrl="https://www.linkedin.com/posts/sukhmani-kaur-ms-odhr-8ba90533_how-do-your-people-interconnect-for-extraordinary-activity-7392964410291683329-JSw0"
          evolve='Ask Gail, AAI&rsquo;s Green agent: "Show me the hidden relational pathways in this challenge I am facing." and see where that one question takes you.'
        />

        {/* TURQUOISE */}
        <ColourSection
          colour="#4D9E9E"
          colourLabel="Turquoise"
          think="Our Words Speak a Thousand Pictures"
          consider="Essence is revealed through language — the words we choose show who we are."
          reflect="What words do you use that reveal what you value at work?"
          expandUrl="https://www.linkedin.com/posts/sukhmani-kaur-ms-odhr-8ba90533_our-words-speak-a-thousand-pictures-activity-7385390846969540608-iivx"
          evolve='Ask Timothy, AAI&rsquo;s Turquoise agent: "What does my language reveal about what I value at work?" and see where that one question takes you.'
        />

        {/* BLUE */}
        <ColourSection
          colour="#4A7FB5"
          colourLabel="Blue"
          think="Inside-Out Planetary System Perplexes Astronomers"
          consider="Perspective shifts often come from anomalies — things that don't fit the model."
          reflect="What anomaly in your work might be trying to teach you something?"
          expandUrl="https://www.linkedin.com/posts/sukhmani-kaur-ms-odhr-8ba90533_inside-out-planetary-system-perplexes-astronomers-activity-7466661806254784512-zaoU"
          evolve='Ask Byron, AAI&rsquo;s Blue agent: "Help me see this situation from a completely different angle." and see where that one question takes you.'
        />

        {/* PURPLE */}
        <ColourSection
          colour="#7B5EA7"
          colourLabel="Purple"
          think="Did We Pave Development Paradise and Put Up a Change Parking Lot?"
          consider="Remember what developing, as an organization and an individual is about — relational, human, systemic — bringing about change for a better future is not just procedural."
          reflect="Where have you, or your organization replaced depth with speed at work, limiting possibilities for future change?"
          expandUrl="https://www.linkedin.com/posts/sukhmani-kaur-ms-odhr-8ba90533_did-we-pave-od-paradise-and-put-up-a-change-activity-7449080420379353088-L7cP"
          evolve='Ask Peter, AAI&rsquo;s Purple agent: "What foundational principle have we drifted away from?" and see where that one question takes you.'
        />
      </div>
    </div>
  );
}

function ColourSection({
  colour,
  colourLabel,
  think,
  consider,
  reflect,
  expandUrl,
  evolve,
}: {
  colour: string;
  colourLabel: string;
  think: string;
  consider: string;
  reflect: string;
  expandUrl: string;
  evolve: string;
}) {
  return (
    <section className="mb-10 rounded-lg border bg-white p-6" style={{ borderLeftWidth: '4px', borderLeftColor: colour }}>
      <h2 className="mb-4 text-lg font-bold" style={{ color: colour }}>
        {colourLabel}
      </h2>
      <div className="space-y-3 text-sm leading-relaxed text-text-secondary">
        <p>
          <span className="font-semibold text-text-primary">Think:</span>{' '}
          {think}
        </p>
        <p>
          <span className="font-semibold text-text-primary">Consider:</span>{' '}
          {consider}
        </p>
        <p>
          <span className="font-semibold text-text-primary">Reflect:</span>{' '}
          {reflect}
        </p>
        <p>
          <span className="font-semibold text-text-primary">Expand your POV:</span>{' '}
          <a
            href={expandUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gail underline hover:opacity-80"
          >
            Read on LinkedIn →
          </a>
        </p>
        <p>
          <span className="font-semibold text-text-primary">Evolve:</span>{' '}
          <span dangerouslySetInnerHTML={{ __html: evolve }} />
        </p>
      </div>
    </section>
  );
}
